import type { AccessorFnColumnDef, ColumnFilter, RowData } from '@tanstack/react-table';
import { z } from 'zod';

import { commonFiltersSchema } from '@apple/utils/filters';
import { paginationStateSchema } from '@apple/utils/pagination/models';

import type { DataTableFeatures } from '../features';
import type { ColumnFilterDef, FieldMap, FilterData, FilterVariant } from '../types';
import { log } from './logger';
import { sortingStateSchema } from './sorting';

export function createDataTableQueryOptionsSchema<
	TFilters extends FilterData<TFilters>,
	TFiltersSchema extends z.ZodType<TFilters> = z.ZodType<TFilters>,
>(filtersSchema: TFiltersSchema) {
	return z.object({
		pagination: paginationStateSchema,
		filters: filtersSchema,
		commonFiltersSchema: commonFiltersSchema.optional(),
		sorting: sortingStateSchema.optional(),
	});
}

export function createFilterOnlyColumn<
	TRowData extends RowData,
	TFilters extends FilterData<TFilters>,
	FilterField extends keyof TFilters & string = keyof TFilters & string,
>(options: FilterOnlyColumnOptions<TFilters, FilterField>) {
	log.debug('createFilterOnlyColumn', options);
	return {
		id: options.field,
		header: options.label,
		accessorFn: () => '' as TFilters[FilterField],
		enableColumnFilter: !options.hidden,
		filter: {
			group: options.group,
			variant: options.variant,
			filterFromString: options.filterFromString,
			getFilterDisplayValue: options.getFilterDisplayValue,
			sortCompareFn: options.sortCompareFn,
			minValue: options.minValue,
			maxValue: options.maxValue,
		},
		meta: {
			hideColumn: true,
		},
		// All columns have a default size. Since these are filter-only columns, we don't want them to take up any space.
		// https://tanstack.com/table/v8/docs/guide/column-sizing#column-widths
		size: 0,
		minSize: 0,
		maxSize: 0,
	} satisfies AccessorFnColumnDef<DataTableFeatures, TRowData, TFilters[FilterField]>;
}

export interface FilterOnlyColumnOptions<
	TFilters extends FilterData<TFilters>,
	FilterField extends keyof TFilters = keyof TFilters,
> {
	field: FilterField;
	group: string;
	label: string;
	variant: FilterVariant;
	// options: TFilters[FilterField][];
	hidden?: boolean;
	sortCompareFn?: (a: unknown, b: unknown) => number;
	// HACK: need to find a better way to handle this...
	filterFromString?: ColumnFilterDef<RowData, TFilters[FilterField]>['filterFromString'];
	// HACK: need to find a better way to handle this...
	getFilterDisplayValue?: ColumnFilterDef<
		RowData,
		TFilters[FilterField]
	>['getFilterDisplayValue'];
	minValue?: unknown;
	maxValue?: unknown;
}

export function getFilterDataFromColumnFilters<
	TRow extends RowData,
	TFilters extends FilterData<TFilters>,
>(columnFilters: ColumnFilter[], mappings: FieldMap<TRow, TFilters>[]): TFilters {
	return columnFilters.reduce((acc, rowFilter) => {
		const mappedId =
			mappings.find(x => x.row == rowFilter.id)?.filter ?? (rowFilter.id as keyof TFilters);
		acc[mappedId] = rowFilter.value as TFilters[keyof TFilters];
		return acc;
	}, {} as TFilters);
}

export function getColumnFiltersFromFilterData<
	TRow extends RowData,
	TFilters extends FilterData<TFilters>,
>(filters: TFilters, mappings: FieldMap<TRow, TFilters>[]): ColumnFilter[] {
	return Object.entries(filters).map(([key, value]) => {
		const mappedId = mappings.find(x => x.filter == key)?.row ?? key;
		return {
			id: mappedId as string,
			value,
		} satisfies ColumnFilter;
	});
}
